import React from "react";

import { useGLTF } from "@react-three/drei";
import { useConfigurationOptionsContext } from "../../../../../app/providers/DoorOptionsProvider";
import { isUHandleCode } from "../../../../../app/providers/doorHandleProfiles";
import { DoorComponentProps } from "../../Door";
import Beam from "../components/Beam";
import Glass from "../components/Glass";
import KomHandle from "../components/KomHandle";
import UHandle from "../components/UHandle";
import { DeIjsselLayout } from "../layouts/DeIjsselLayout";
import { DeMaasLayout } from "../layouts/DeMaasLayout";
import { DeWaalCutLayouts } from "../layouts/DeWaalCutLayouts";
import { DeWaalLayoutXLXXL } from "../layouts/DeWaalLayoutXLXXL";
import Frame from "./Frame";
import Rails from "./Rails";
import { getKomHandleAdjustments, getScaleRails } from "./SchuifHelper";
import { SchuifGLTFResult } from "./SchuifType";
import { getFullAssetPath } from "shared/utils/assets";
import { SchuifVerticalLayout } from "../layouts/SchuifVerticalLayout";
import { type VerticalBeamPosition } from "entities/configurator/model/layoutMeta";

interface SchuifProps extends DoorComponentProps {
  verticalBeamPosition?: VerticalBeamPosition;
}

const Schuif: React.FC<SchuifProps> = ({
  materials,
  position,
  rotation,
  index = 0,
  frameSettings,
  railSettings,
  rotateNewlayouts = false,
  verticalBeamPosition,
}: SchuifProps) => {
  const { nodes } = useGLTF(
    getFullAssetPath("/objects-v3/schuifdeur_v6.glb"),
  ) as SchuifGLTFResult;

  const {
    handle,
    getConfigurationBeams,
    getConfigurationWidth,
    getConfigurationHeight,
    getConfigurationColor,
    isDeMaasLayout,
    isDeWaalLayout,
    isDeIjsselLayout,
    selectedDoor,
    rotateDeWaal23,
    rotateDeMaas2Dubbel,
    isLeafStyleDeWaalLayout,
  } = useConfigurationOptionsContext();

  const height = getConfigurationHeight(index);

  //Chosen material
  const chosenMaterial = materials[getConfigurationColor()];

  const width = getConfigurationWidth(index);
  const deWaalCutLayoutOffsetY = -0.025;

  const scaleRails = getScaleRails(width, selectedDoor);

  const { handleXAdjustment } = getKomHandleAdjustments(width, selectedDoor);
  const isVerticalHandle = isUHandleCode(handle?.code);
  const configuredBeams = getConfigurationBeams(!isVerticalHandle);

  const schuifScaleNewLayouts = isDeWaalLayout
    ? 1
    : (frameSettings?.generalScaleY ?? 1);
  return (
    <group dispose={null} position={position} rotation={rotation}>
      <group rotation-y={rotateNewlayouts ? Math.PI : 0}>
        {isDeMaasLayout && (
          <group rotation-y={rotateDeMaas2Dubbel ? Math.PI : 0}>
            <DeMaasLayout
              nodes={nodes}
              chosenMaterial={chosenMaterial}
              doorIndex={index}
            />
          </group>
        )}

        {/* De Waal */}
        {isDeWaalLayout && (
          <group rotation-y={rotateDeWaal23 ? Math.PI : 0}>
            {isLeafStyleDeWaalLayout ? (
              <group position-y={deWaalCutLayoutOffsetY}>
                <DeWaalCutLayouts
                  nodes={nodes}
                  chosenMaterial={chosenMaterial}
                  doorIndex={index}
                />
              </group>
            ) : (
              <DeWaalLayoutXLXXL
                nodes={nodes}
                chosenMaterial={chosenMaterial}
                doorIndex={index}
              />
            )}
          </group>
        )}

        {isDeIjsselLayout && (
          <DeIjsselLayout
            nodes={nodes}
            chosenMaterial={chosenMaterial}
            doorIndex={index}
          />
        )}
      </group>

      <group position-y={frameSettings?.generalShiftElementsY ?? 0}>
        <group scale-x={scaleRails} position-x={-0.001}>
          <Rails
            doorNodes={nodes}
            materials={materials}
            index={index}
            railSettings={railSettings}
          />
        </group>
        <group
          position-z={frameSettings?.generalShiftZ ?? 0}
          position-y={
            frameSettings?.generalShiftY
              ? frameSettings?.generalShiftY +
                (height >= 1900 && height <= 2200
                  ? 0.02
                  : height >= 2300 && height <= 2600
                    ? 0.01
                    : 0)
              : 0
          }
          scale-y={schuifScaleNewLayouts}
        >
          <group
            scale-y={
              frameSettings?.addFrameScaleY
                ? height >= 1900 && height <= 2200
                  ? frameSettings?.addFrameScaleY
                  : height >= 2300 && height <= 2600
                    ? frameSettings?.addFrameScaleY - 0.002
                    : frameSettings?.addFrameScaleY - 0.005
                : 1
            }
          >
            <Frame
              doorNodes={nodes}
              materials={materials}
              index={index}
              {...frameSettings}
            />
            <SchuifVerticalLayout
              nodes={nodes}
              material={chosenMaterial}
              width={width}
              height={height}
              semanticPosition={verticalBeamPosition}
              frameSettings={frameSettings}
            />
          </group>
          <group
            position-z={-(frameSettings?.generalShiftZ ?? 0)}
            scale-x={1.04}
          >
            <group
              position-y={frameSettings?.generalShiftZ ? -0.015 : 0}
              scale-y={frameSettings?.generalGlassScaleY ?? 1}
            >
              <Glass doorNodes={nodes} materials={materials} index={index} />
            </group>

            {isVerticalHandle ? (
              <group position-x={handleXAdjustment}>
                <UHandle
                  doorNodes={nodes}
                  materials={materials}
                  index={index}
                  shiftXValue={frameSettings?.UHandleShiftX ?? 0}
                  showBackHandle={frameSettings?.UHandleShowBackHandle}
                  schuifdeur={frameSettings?.schuifdeurHandleShift ?? true}
                  reverse={frameSettings?.UHandleReversePos}
                />
              </group>
            ) : (
              <group
                rotation-y={frameSettings?.KomHandleGeneralRotationY ?? 0}
                position-x={frameSettings?.KomHandleGeneralPositionX ?? 0}
              >
                <group position-x={handleXAdjustment}>
                  <KomHandle
                    doorNodes={nodes}
                    materials={materials}
                    index={index}
                    shiftZValue={frameSettings?.KomHandleGeneralShiftZ ?? 0}
                    showKomGreepBack={frameSettings?.KomHandleShow ?? true}
                  />
                </group>
              </group>
            )}
            {configuredBeams.map((beam, i) => {
              return (
                <Beam
                  id={beam.id}
                  key={`schuif-beam-${i}`}
                  doorNodes={nodes}
                  materials={materials}
                  width={getConfigurationWidth(index)}
                  height={beam.height / 1000}
                />
              );
            })}
          </group>
        </group>
      </group>
    </group>
  );
};

export default Schuif;
